import React, { useEffect, useRef, useState } from 'react';
import { paintWallpaper } from './wallpaper';
import { FRAGMENT_SHADER, VERTEX_SHADER } from './shaders';
import styles from './CRT.module.css';

interface CRTBackgroundProps {
    enabled: boolean;
    scanlines: number;
    curvature: number;
    noise: number;
}

const FRAME_MS = 1000 / 30;
const MAX_DPR = 1.5;
const WALLPAPER_PIXEL = 3;

const compile = (gl: WebGLRenderingContext, type: number, source: string) => {
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) ?? 'shader compile failed');
    }
    return shader;
};

/**
 * 바탕 CRT 튜브. 디더 벽지를 셰이더로 통과시켜 곡면, 주사선, 애퍼처 그릴, 재생 띠를 그린다.
 * React 상태 없이 rAF 하나로 돈다.
 */
export const CRTBackground: React.FC<CRTBackgroundProps> = ({ enabled, scanlines, curvature, noise }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const propsRef = useRef({ enabled, scanlines, curvature, noise });
    const redrawRef = useRef<() => void>(() => { });
    const [generation, setGeneration] = useState(0);

    useEffect(() => {
        propsRef.current = { enabled, scanlines, curvature, noise };
        redrawRef.current();
    }, [enabled, scanlines, curvature, noise]);

    useEffect(() => {
        const canvas = canvasRef.current!;
        const gl = canvas.getContext('webgl', { alpha: false, antialias: false, powerPreference: 'low-power' });
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        // WebGL이 없으면 벽지만 그대로 보여준다
        if (!gl) {
            const wallpaper = paintWallpaper(canvas.clientWidth, canvas.clientHeight, WALLPAPER_PIXEL);
            canvas.width = wallpaper.width;
            canvas.height = wallpaper.height;
            canvas.style.imageRendering = 'pixelated';
            canvas.getContext('2d')?.drawImage(wallpaper, 0, 0);
            return;
        }

        const program = gl.createProgram()!;
        gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER));
        gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER));
        gl.linkProgram(program);
        gl.useProgram(program);

        gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        const position = gl.getAttribLocation(program, 'aPosition');
        gl.enableVertexAttribArray(position);
        gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

        const uniform = (name: string) => gl.getUniformLocation(program, name);
        const u = {
            resolution: uniform('uResolution'),
            dpr: uniform('uDpr'),
            time: uniform('uTime'),
            scan: uniform('uScan'),
            curve: uniform('uCurve'),
            noise: uniform('uNoise'),
            on: uniform('uOn'),
        };

        gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        let rafId = 0;
        let lastFrame = 0;
        const startedAt = performance.now();

        const draw = (now: number) => {
            const props = propsRef.current;
            gl.uniform1f(u.time, (now - startedAt) / 1000);
            gl.uniform1f(u.scan, props.scanlines);
            gl.uniform1f(u.curve, props.curvature);
            gl.uniform1f(u.noise, props.noise);
            gl.uniform1f(u.on, props.enabled ? 1 : 0);
            gl.drawArrays(gl.TRIANGLES, 0, 3);
        };

        const loop = (now: number) => {
            rafId = requestAnimationFrame(loop);
            if (now - lastFrame < FRAME_MS) return;
            lastFrame = now;
            draw(now);
        };

        const sync = () => {
            cancelAnimationFrame(rafId);
            rafId = 0;
            if (propsRef.current.enabled && !document.hidden && !reducedMotion.matches) {
                rafId = requestAnimationFrame(loop);
            } else {
                draw(performance.now());
            }
        };
        redrawRef.current = sync;

        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
            const width = canvas.clientWidth;
            const height = canvas.clientHeight;
            if (!width || !height) return;
            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            gl.viewport(0, 0, canvas.width, canvas.height);
            gl.uniform2f(u.resolution, canvas.width, canvas.height);
            gl.uniform1f(u.dpr, dpr);
            gl.texImage2D(
                gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE,
                paintWallpaper(width, height, WALLPAPER_PIXEL),
            );
            draw(performance.now());
        };

        // 휴대폰이 백그라운드에서 GPU를 회수하면, 복구될 때 캔버스를 새로 만든다
        const onContextLost = (event: Event) => {
            event.preventDefault();
            cancelAnimationFrame(rafId);
        };
        const onContextRestored = () => setGeneration((value) => value + 1);

        canvas.addEventListener('webglcontextlost', onContextLost);
        canvas.addEventListener('webglcontextrestored', onContextRestored);
        const observer = new ResizeObserver(resize);
        observer.observe(canvas);
        document.addEventListener('visibilitychange', sync);
        reducedMotion.addEventListener('change', sync);
        resize();
        sync();

        return () => {
            cancelAnimationFrame(rafId);
            canvas.removeEventListener('webglcontextlost', onContextLost);
            canvas.removeEventListener('webglcontextrestored', onContextRestored);
            observer.disconnect();
            document.removeEventListener('visibilitychange', sync);
            reducedMotion.removeEventListener('change', sync);
            redrawRef.current = () => { };
        };
    }, [generation]);

    return <canvas key={generation} ref={canvasRef} className={styles.tube} aria-hidden="true" />;
};

export default CRTBackground;
